import { useEffect,useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import ArtworkConfirmationDialog from './components/ArtworkConfirmationDialog';
import ChangesRequiredDisplayer from './components/ChangesRequiredDisplayer';
import SaveDraft from './components/SaveDraft';
import StepAccordeon from './components/StepAccordeon';
import About from './about';
import Media from './media';
import Shipping from './shipping';
import SubmitButton from './submit-button';

import './index.css';

export default function AddArtworkForm({ artworkData = null, previewMode = false, editMode = false }) {
  const { state, stateHandler, cacheHandler } = useStateHandler();
  const navigate = useNavigate();
  
  // State for confirmation dialog when leaving
  const [goBackConfirmation, setGoBackConfirmation] = useState(false);

  // Form state
  const [formState, setFormState] = useState(Utils.initArtworkFormState(artworkData, editMode, state));
  
  // State for artwork agreement acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // State for form validation errors
  const [error, setError] = useState(null);
  
  // Stores the initial form state for comparison
  const initialState = useRef(formState);
  
  // Hook for navigating back
  const goBackHandler = useGoBack("/profile");

  // ✅ Prevent page refresh or closing tab
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ''; // Standard browser warning
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <>
      <div className="add-artwork-form__container">
        <div className="add-artwork-form__wrap">
          <div className="add-artwork-form__header">
            
            {/* Exit Button with Change Detection */}
            <XIcon 
              className="add-artwork-form__back-button" 
              onClick={() => {
                const hasChanges = JSON.stringify(initialState.current) !== JSON.stringify(formState);
                
                if (hasChanges) {
                  setGoBackConfirmation(true);
                } else {
                  goBackHandler();
                }
              }}
              textClose={true}
            />

            <Text className="add-artwork-form__header-text" large>
              {previewMode ? "Artwork request" : editMode ? "Edit artwork" : "Add an artwork"}
            </Text>

            {(!previewMode && !editMode) && (
              <SaveDraft 
                formState={formState} 
                setFormState={setFormState} 
                initialState={initialState}
                buttonFormat={false}
              />
            )}
          </div>

          {/* Show required changes if artworkData contains them */}
          {Boolean(artworkData?.draft_changes) && <ChangesRequiredDisplayer changesRequired={artworkData.draft_changes}/>}

          <Text className="add-artwork-form__text" paragraph small>
            Suarte is currently accepting paintings and wall sculptures.
          </Text>

          <form className="add-artwork-form__form" id="add-artwork-form">
            
            {/* Step 1: About */}
            <StepAccordeon 
              lastCompletedStep={formState.lastCompletedStep} 
              previewMode={previewMode} 
              editMode={editMode} 
              step={1} 
              header="About" 
              activeClassName="add-artwork-about-accordeon__active" 
              formError={error} 
              elements={["artwork_artist","artwork_title","artwork_description","artwork_medium","artwork_year","artwork_size","artwork_height","artwork_length","artwork_theme","artwork_feeling","artwork_color","artwork_currency","artwork_price"]}
            >
              <About 
                formState={formState} 
                setFormState={setFormState} 
                editMode={editMode} 
                previewMode={previewMode} 
                formError={error} 
                setFormError={setError}
              />
            </StepAccordeon>

            {/* Step 2: Media */}
            <StepAccordeon 
              lastCompletedStep={formState.lastCompletedStep} 
              previewMode={previewMode} 
              editMode={editMode} 
              step={2} 
              header="Media" 
              activeClassName="add-artwork-media-accordeon__active" 
              formError={error} 
              elements={["artwork_main_picture"]}
            >
              <Media 
                formState={formState} 
                setFormState={setFormState} 
                editMode={editMode} 
                previewMode={previewMode} 
                formError={error} 
                setFormError={setError}
              />
            </StepAccordeon>

            {/* Step 3: Shipping */}
            <StepAccordeon 
              lastCompletedStep={formState.lastCompletedStep} 
              previewMode={previewMode} 
              editMode={editMode} 
              step={3} 
              header="Shipping" 
              activeClassName="add-artwork-shipping-accordeon__active" 
              formError={error}
            >
              <Shipping 
                formState={formState} 
                setFormState={setFormState} 
                editMode={editMode} 
                previewMode={previewMode} 
                formError={error} 
                setFormError={setError} 
              />
            </StepAccordeon>

            {/* Submit Button */}
            {!previewMode && (
              <SubmitButton
                formState={formState} 
                formError={error} 
                setFormError={setError} 
                editMode={editMode} 
                termsAccepted={termsAccepted} 
                setTermsAccepted={setTermsAccepted} 
                setGoBackConfirmation={setGoBackConfirmation}
              />
            )}
          </form>
        </div>
      </div>

      {/* Artwork Confirmation Dialog */}
      <ArtworkConfirmationDialog 
        open={goBackConfirmation} 
        onClose={() => {
          setGoBackConfirmation(false); 
        }}
        onConfirm={goBackHandler} 
        confirmText="Yes, exit"
        formState={formState} 
        setFormState={setFormState} 
        initialState={initialState}
        previewMode={previewMode}
        editMode={editMode}
        formError={error}
        setFormError={setError} 
        navigate={navigate}
        cacheHandler={cacheHandler}
        stateHandler={stateHandler}
      >
        <Text className="add-artwork-form__confirmation-text-header" paragraph small>
          Exit without saving?
        </Text>

        <Text className="add-artwork-form__confirmation-text" paragraph small>
          {editMode ? "If you exit now, your changes will be lost." : "Exiting now will lose your changes. Save draft and continue later."}
        </Text>
      </ArtworkConfirmationDialog>
    </>
  );
}